import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { fetchPageBySlug } from '../lib/cms';
import Gallery from '../components/Gallery';
import InteriorGallery from '../components/InteriorGallery';

export default function Interior() {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPage() {
      const data = await fetchPageBySlug('interioerfolie');
      setPage(data);
      setLoading(false);
    }
    loadPage();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-red border-t-transparent" />
      </div>
    );
  }

  const examples = [
    {
      title: 'Restauranter & Caféer',
      desc: 'Perfekt til bardiske, caféborde, receptionsskranker og buffetstationer, der trænger til et nyt udtryk uden lange lukkedage.',
      points: ['Bardiske og betjeningsskranker', 'Café- og restaurantborde', 'Skillevægge og søjler']
    },
    {
      title: 'Køkken & Bad',
      desc: 'Forny køkkenlåger, fronter og bordplader hurtigt og enkelt – uden støv, larm eller omkostningen ved et helt nyt køkken.',
      points: ['Køkkenlåger og skuffer', 'Slidstærke bordplader', 'Badeværelsesmøbler og skabe']
    },
    {
      title: 'Skriveborde & Kontor',
      desc: 'Giv kontorets hæve-/sænkeborde, mødeborde og reoler en ensartet og moderne overflade, der matcher virksomhedens stil.',
      points: ['Hæve-/sænkeborde og mødeborde', 'Reoler og opbevaring', 'Receptions- og venteområder']
    }
  ];

  const heroImage =
    (page?.hero_billede && (Array.isArray(page.hero_billede) ? page.hero_billede[0] : page.hero_billede)) ||
    (page?.hero_image && (Array.isArray(page.hero_image) ? page.hero_image[0] : page.hero_image)) ||
    (page?.billeder && page.billeder[0]) ||
    (page?.images && page.images[0]) ||
    'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/img-4357.jpeg';
  const heroImageAlt = page?.hero_billede_alt || page?.hero_image_alt || 'CRE8IT Interiørfolie køkken';
  const secondaryImage =
    (page?.mellem_billede && (Array.isArray(page.mellem_billede) ? page.mellem_billede[0] : page.mellem_billede)) ||
    (page?.sekundaert_billede && (Array.isArray(page.sekundaert_billede) ? page.sekundaert_billede[0] : page.sekundaert_billede)) ||
    (page?.secondary_image && (Array.isArray(page.secondary_image) ? page.secondary_image[0] : page.secondary_image)) ||
    (page?.billeder && page.billeder[1]) ||
    (page?.images && page.images[1]) ||
    'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/img-3497-2.jpeg';
  const secondaryImageAlt =
    page?.mellem_billede_alt ||
    page?.sekundaert_billede_alt ||
    page?.secondary_image_alt ||
    'Montering af interiørfolie';

  return (
    <div className="pb-24 space-y-16 lg:space-y-20 bg-white">
      <Helmet>
        <title>{page?.meta_titel || page?.meta_title || 'Interiørfolie - Bæredygtig fornyelse af inventar | CRE8IT'}</title>
        <meta
          name="description"
          content={
            page?.meta_beskrivelse ||
            page?.meta_description ||
            'Professionel montering af interiørfolie til køkkener, døre, borde og inventar. Bæredygtig fornyelse til erhverv og privat hos CRE8IT.'
          }
        />
      </Helmet>

      {/* 1. Hero */}
      <section className="relative overflow-hidden bg-[#325d52] py-16 lg:py-24 border-b border-[#245b53]/40 text-white">
        {/* Subtile dekorative lysglød */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-24 right-10 h-96 w-96 rounded-full bg-[#deecd4]/15 blur-3xl" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
            
            {/* Tekst */}
            <div className="space-y-6 lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#deecd4] px-3.5 py-1 text-xs font-bold text-[#325d52] border border-[#deecd4] shadow-xs">
                <i className="fa-solid fa-layer-group" aria-hidden="true" />
                <span>Bæredygtig Renovering & Genbrug</span>
              </div>

              <h1 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
                {page?.overskrift || page?.headline || (
                  <>
                    INTERIØR <span className="text-[#deecd4]">FOLIE</span>
                  </>
                )}
              </h1>
              <div className="flex items-center gap-1.5 pt-0.5">
                <div className="h-1.5 w-12 rounded-full bg-[#deecd4]" />
                <div className="h-1.5 w-3 rounded-full bg-white/40" />
                <div className="h-1.5 w-1.5 rounded-full bg-[#deecd4]" />
              </div>
              
              <p className="text-base sm:text-lg text-emerald-50/90 leading-relaxed max-w-2xl">
                {page?.underoverskrift || page?.subtitle || 'Giv dine køkkener, døre, borde og inventar et helt nyt liv med slidstærk kvalitetsfolie.'}
              </p>

              <div className="flex flex-wrap gap-3 pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#deecd4] px-6 py-3.5 text-sm font-bold text-[#1b453f] shadow-md transition-all hover:bg-white hover:text-gray-900 cursor-pointer"
                >
                  <i className="fa-solid fa-envelope text-xs text-[#1b453f]" aria-hidden="true" />
                  Kontakt for tilbud
                </Link>
                <a
                  href="#galleri"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/60 bg-white/15 backdrop-blur-xs px-5 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-white hover:text-gray-900 cursor-pointer group"
                >
                  <i className="fa-solid fa-images text-xs text-[#deecd4] group-hover:text-gray-900 transition-colors" aria-hidden="true" />
                  Se eksempler på forvandlinger
                </a>
              </div>
            </div>

            {/* Billede */}
            <div className="relative lg:col-span-5">
              <div className="aspect-[4/3] sm:aspect-square overflow-hidden rounded-3xl border border-gray-100 shadow-xl bg-white">
                <img
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/${heroImage.replace('https://assets.auxodev.dk/', '')}`}
                  alt={heroImageAlt}
                  className="h-full w-full object-cover"
                />
              </div>

              {/* Dekorativ garantibadge - halvt over billedet */}
              <div className="absolute -bottom-5 -right-5 rounded-2xl bg-white p-4 shadow-xl border border-gray-100 flex items-center gap-3 z-10">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-green-light/40 text-brand-red">
                  <i className="fa-solid fa-shield-check text-xl" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Garanti på folien
                  </p>
                  <p className="text-sm sm:text-base font-bold text-gray-900 leading-tight">
                    10 års garanti
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 2. Hvad bruges det til? (Enkle, rene eksempler) */}
      <section id="eksempler" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-8 scroll-mt-24">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Hvad bruges <span className="text-brand-red">interiørfolie</span> til?
          </h2>
          <div className="flex items-center gap-1.5 pt-1.5">
            <div className="h-1 w-10 rounded-full bg-brand-red" />
            <div className="h-1 w-3 rounded-full bg-brand-green-light" />
            <div className="h-1 w-1.5 rounded-full bg-brand-red" />
          </div>
          <p className="mt-2 text-sm sm:text-base text-gray-600 max-w-3xl">
            Folien kan monteres på de fleste glatte og faste overflader. Her er de tre mest almindelige anvendelsesområder:
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {examples.map((item, idx) => (
            <div
              key={idx}
              className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-7 shadow-xs flex flex-col justify-between overflow-hidden relative group hover:border-brand-red/40 transition-colors"
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-red via-[#2bb89a] to-brand-green-light" />
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.desc}
                </p>

                <ul className="space-y-2 pt-2 border-t border-gray-100">
                  {item.points.map((point, pIdx) => (
                    <li key={pIdx} className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                      <i className="fa-solid fa-check text-brand-red text-xs mt-1 shrink-0" aria-hidden="true" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="pt-6 mt-4 border-t border-gray-100">
                <Link
                  to="/kontakt"
                  className="text-xs sm:text-sm font-semibold text-brand-red hover:text-brand-red-hover inline-flex items-center gap-1.5"
                >
                  <span>Få en pris</span>
                  <i className="fa-solid fa-arrow-right text-[11px]" aria-hidden="true" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 3. Om interiørfolie og montering */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:items-stretch">
          
          <div className="lg:col-span-5 order-last lg:order-first flex">
            <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-sm w-full h-full min-h-[280px]">
              <img
                src={`https://assets.auxodev.dk/cdn-cgi/image/width=700,quality=85,format=auto/${secondaryImage.replace('https://assets.auxodev.dk/', '')}`}
                alt={secondaryImageAlt}
                className="h-full w-full object-cover"
              />
            </div>
          </div>

          <div className="lg:col-span-7 space-y-4 flex flex-col justify-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Professionel montering med <span className="text-brand-red">mange års erfaring</span>
            </h2>
            <div className="flex items-center gap-1.5 pt-0.5">
              <div className="h-1 w-10 rounded-full bg-brand-red" />
              <div className="h-1 w-2.5 rounded-full bg-brand-green-light" />
            </div>
            
            <div className="prose max-w-none text-gray-700 text-sm sm:text-base leading-relaxed space-y-3">
              {page?.indhold || page?.content ? (
                <div dangerouslySetInnerHTML={{ __html: page.indhold || page.content }} />
              ) : (
                <>
                  <p>
                    Interiørfolie er et fantastisk produkt, der giver dine møbler, køkkener, døre og andet inventar et helt nyt look. I stedet for at udskifte velfungerende elementer, kan du med folie skabe præcis den stil og atmosfære, du ønsker.
                  </p>
                  <p>
                    Vi arbejder med markedets førende kvalitetsfolier med autentiske overfladestrukturer såsom træ, sten, beton, marmor, metal og et bredt udvalg af matte og blanke farver.
                  </p>
                  <p>
                    Folien er ekstremt slidstærk, rengøringsvenlig og monteres hurtigt og præcist på stedet – helt uden støv, larm og byggerod.
                  </p>
                </>
              )}
            </div>

            {(page?.specifikationer || page?.specifications) && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                <div
                  className="prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_li]:text-gray-700"
                  dangerouslySetInnerHTML={{ __html: page.specifikationer || page.specifications }}
                />
              </div>
            )}

            <div className="pt-2">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 text-sm font-semibold text-brand-red hover:text-brand-red-hover"
              >
                <span>Har du et projekt? Tag en snak med os</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* 4. Galleri & Før/Efter Cases */}
      <section id="galleri" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 border-t border-gray-200 pt-14">
        <InteriorGallery />
      </section>
    </div>
  );
}
